import type { ReactNode } from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { useTranslation } from '@/i18n/LanguageContext';
import { dispatchOnboardingEvent } from '@/components/onboarding/uiAdapter';
import { useEntityDetailsDialog } from '@/contexts/useEntityDetailsDialog';

interface Props {
  groupId: string;
  groupName?: string;
  children: ReactNode;
  onEnter: (groupId: string) => void;
  onFocusProperty: (groupId: string, field: 'name' | 'description' | 'color') => void;
  onToggleCollapse?: (groupId: string) => void;
  onDelete?: (groupId: string) => void;
  disabled?: boolean;
}

/**
 * Right-click menu for collapsed layer cards (CollapsedGroupCard). Mirrors
 * BlockContextMenu but exposes layer-specific actions (Enter, Expand back
 * into a group, Delete). Radix's Slot needs a real DOM node so we wrap the
 * children in a plain <div>.
 */
export function LayerContextMenu({
  groupId,
  groupName,
  children,
  onEnter,
  onFocusProperty,
  onToggleCollapse,
  onDelete,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const { openDetails } = useEntityDetailsDialog();

  if (disabled) return <>{children}</>;

  const withDispatch = (action: string, fn: () => void) => () => {
    dispatchOnboardingEvent({ name: 'layer:contextActionSelected', target: action });
    fn();
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          onContextMenu={() => {
            dispatchOnboardingEvent({ name: 'layer:rightclick', target: groupId });
          }}
        >
          {children}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        <ContextMenuItem
          data-guide="ctx-enter-layer"
          onSelect={withDispatch('ctx-enter-layer', () => onEnter(groupId))}
        >
          {t('enterLayer') || 'Enter layer'}
        </ContextMenuItem>

        <ContextMenuSeparator />

        <ContextMenuItem
          data-guide="ctx-rename"
          onSelect={withDispatch('ctx-rename', () => onFocusProperty(groupId, 'name'))}
        >
          {t('editName') || t('rename') || 'Edit name'}
        </ContextMenuItem>
        <ContextMenuItem
          data-guide="ctx-color"
          onSelect={withDispatch('ctx-color', () => onFocusProperty(groupId, 'color'))}
        >
          {t('editColor') || t('changeColor') || 'Edit color'}
        </ContextMenuItem>
        <ContextMenuItem
          data-guide="ctx-details"
          onSelect={withDispatch('ctx-details', () => openDetails({
            entityId: groupId, entityType: 'layer', entityName: groupName,
          }))}
        >
          {t('details') || 'Details…'}
        </ContextMenuItem>

        {onToggleCollapse && (
          <ContextMenuItem
            data-guide="ctx-expand-layer"
            onSelect={withDispatch('ctx-expand-layer', () => onToggleCollapse(groupId))}
          >
            {t('expandLayer') || 'Expand (convert back to group)'}
          </ContextMenuItem>
        )}

        {onDelete && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              data-guide="ctx-delete"
              onSelect={withDispatch('ctx-delete', () => onDelete(groupId))}
              className="text-destructive focus:text-destructive"
            >
              {t('delete') || 'Delete layer'}
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
